(function (global) {
  'use strict';

  if (!global.customElements || !global.HTMLElement || global.customElements.get('bodycare-wishlist-page')) return;

  class BodycareWishlistPage extends global.HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.emptyState = this.querySelector('[data-wishlist-empty]');
      this.loadButton = this.querySelector('[data-wishlist-load-more]');
      this.sentinel = this.querySelector('[data-wishlist-sentinel]');
      this.batchSize = Math.max(1, Number(this.dataset.batchSize) || 12);
      this.rootUrl = this.dataset.rootUrl || '/';
      this.cursor = 0;
      this.loading = false;
      this.onWishlistChange = this.onWishlistChange.bind(this);
      this.onLoadMore = this.onLoadMore.bind(this);
      global.addEventListener('bodycare:wishlist:changed', this.onWishlistChange);
      if (this.loadButton) this.loadButton.addEventListener('click', this.onLoadMore);
      this.renderFromStorage();
      this.observeSentinel();
    }

    disconnectedCallback() {
      global.removeEventListener('bodycare:wishlist:changed', this.onWishlistChange);
      if (this.loadButton) this.loadButton.removeEventListener('click', this.onLoadMore);
      if (this.observer) this.observer.disconnect();
      this.initialized = false;
    }

    get wishlist() {
      return global.BodycareWishlist;
    }

    renderFromStorage() {
      if (!this.grid || !this.wishlist) return;
      this.savedItems = this.wishlist.getAll();
      this.grid.replaceChildren();
      this.cursor = 0;
      this.updateEmptyState();
      if (this.savedItems.length) this.loadNextBatch();
    }

    updateEmptyState() {
      const isEmpty = !this.savedItems || this.savedItems.length === 0;
      if (this.emptyState) this.emptyState.hidden = !isEmpty;
      if (this.grid) this.grid.hidden = isEmpty;
      if (this.loadButton) this.loadButton.hidden = isEmpty || this.cursor >= (this.savedItems || []).length;
      if (this.sentinel) this.sentinel.hidden = isEmpty || this.cursor >= (this.savedItems || []).length;
    }

    onWishlistChange(event) {
      const detail = event.detail || {};
      if (detail.action === 'remove' && detail.handle) {
        const removedIndex = (this.savedItems || []).findIndex((item) => item.handle === detail.handle);
        const card = this.grid && this.grid.querySelector('[data-wishlist-product-handle="' + detail.handle + '"]');
        if (card) card.remove();
        this.savedItems = this.wishlist.getAll();
        if (removedIndex !== -1 && removedIndex < this.cursor) this.cursor -= 1;
        this.cursor = Math.min(this.cursor, this.savedItems.length);
        this.updateEmptyState();
        return;
      }
      if (detail.action === 'add' || detail.action === 'sync') this.renderFromStorage();
    }

    onLoadMore() {
      this.loadNextBatch();
    }

    observeSentinel() {
      if (!this.sentinel || !('IntersectionObserver' in global)) return;
      this.observer = new global.IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNextBatch();
      }, { rootMargin: '500px 0px' });
      this.observer.observe(this.sentinel);
    }

    makeSkeleton(handle) {
      const item = global.document.createElement('div');
      item.className = 'bodycare-wishlist-page__item is-loading';
      item.dataset.wishlistProductHandle = handle;
      item.setAttribute('aria-busy', 'true');
      item.innerHTML = '<div class="bodycare-wishlist-skeleton"><span></span><span></span><span></span></div>';
      return item;
    }

    async loadNextBatch() {
      if (this.loading || !this.savedItems || this.cursor >= this.savedItems.length) return;
      this.loading = true;
      const batch = this.savedItems.slice(this.cursor, this.cursor + this.batchSize);
      this.cursor += batch.length;
      const slots = batch.map((item) => {
        const slot = this.makeSkeleton(item.handle);
        this.grid.appendChild(slot);
        return { item: item, slot: slot };
      });
      this.updateEmptyState();

      let nextIndex = 0;
      const worker = async () => {
        while (nextIndex < slots.length) {
          const current = slots[nextIndex++];
          await this.loadProduct(current.item.handle, current.slot);
        }
      };
      await Promise.all(Array.from({ length: Math.min(4, slots.length) }, worker));
      this.loading = false;
      this.updateEmptyState();
      this.dispatchEvent(new global.CustomEvent('bodycare:content:loaded', { bubbles: true, detail: { container: this.grid } }));
    }

    async loadProduct(handle, slot) {
      const endpoint = this.rootUrl + 'products/' + encodeURIComponent(handle) + '?section_id=wishlist-product-card';
      try {
        const response = await global.fetch(endpoint, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (response.status === 404) {
          this.wishlist.remove(handle);
          slot.remove();
          return;
        }
        if (!response.ok) throw new Error('HTTP ' + response.status);
        const html = await response.text();
        const template = global.document.createElement('template');
        template.innerHTML = html.trim();
        const card = template.content.querySelector('[data-wishlist-product-handle]');
        if (!card) throw new Error('Wishlist card was not returned');
        slot.replaceWith(card);
      } catch (error) {
        slot.classList.remove('is-loading');
        slot.removeAttribute('aria-busy');
        slot.innerHTML = '';
        const message = global.document.createElement('p');
        message.textContent = 'This item could not be loaded.';
        const retry = global.document.createElement('button');
        retry.type = 'button';
        retry.className = 'btn btn--secondary';
        retry.textContent = 'Retry';
        retry.addEventListener('click', () => {
          slot.replaceChildren();
          slot.classList.add('is-loading');
          slot.setAttribute('aria-busy', 'true');
          this.loadProduct(handle, slot);
        });
        slot.append(message, retry);
      }
    }
  }

  global.customElements.define('bodycare-wishlist-page', BodycareWishlistPage);
})(window);
